import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { axiosAuth } from "../../utils/axiosInstance";
import UserAccountLayout from "./userAccount/UserAccountLayout";
import BookingRow from "./userAccount/BookingRow";
import {
  normalizeDate,
  getTodayKey,
} from "./userAccount/userAccountUtils";

const Userdashboard = () => {
  const [courtBookings, setCourtBookings] = useState([]);
  const [totalBooked, setTotalBooked] = useState(0);
  const [totalPayments, setTotalPayments] = useState("0.00");
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = getTodayKey();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosAuth.get("my-bookings");

        if (response.status === 200 && Array.isArray(response.data.bookings)) {
          const mappedBookings = response.data.bookings.map((booking) => ({
            ...booking,
            courtName: booking.court,
            payment: booking.price,
            type: booking.court ? "court" : booking.type,
          }));
          const courts = [...mappedBookings]
            .reverse()
            .filter(
              (booking) =>
                booking.type === "court" && booking.status === "Success",
            );

          setCourtBookings(courts);
          setTotalBooked(courts.length);
          setTotalPayments(
            courts
              .reduce((sum, b) => sum + parseFloat(b.payment || 0), 0)
              .toFixed(2),
          );

          setTodaysBookings(
            courts.filter((b) => normalizeDate(b.date) === today),
          );
          setUpcomingBookings(
            courts.filter((b) => normalizeDate(b.date) > today),
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [today]);

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }

    try {
      await axiosAuth.get(`/cancel-booking/${bookingId}`);
      const filterOut = (list) => list.filter((b) => b.bookingId !== bookingId);
      const updatedCourts = filterOut(courtBookings);

      setTodaysBookings(filterOut);
      setUpcomingBookings(filterOut);
      setCourtBookings(updatedCourts);
      setTotalBooked(updatedCourts.length);
      setTotalPayments(
        updatedCourts
          .reduce((sum, b) => sum + parseFloat(b.payment || 0), 0)
          .toFixed(2),
      );
      alert("Booking cancelled successfully");
    } catch (error) {
      console.error("Error cancelling booking:", error);
      alert(
        "Failed to cancel booking: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  return (
    <UserAccountLayout
      active="dashboard"
      title="Dashboard"
      subtitle="Your bookings, payments, and upcoming sessions at a glance."
      actions={
        <Link to="/all-facilities" className="ua-btn ua-btn--primary">
          <i className="fa-solid fa-plus" aria-hidden="true" />
          Book a court
        </Link>
      }
    >
      <section className="ua-stats" aria-label="Booking statistics">
        <div className="ua-stat">
          <span className="ua-stat__icon" aria-hidden="true">
            <i className="fa-solid fa-calendar-check" />
          </span>
          <div className="ua-stat__body">
            <span className="ua-stat__label">Total bookings</span>
            <span className="ua-stat__value">{totalBooked}</span>
          </div>
        </div>
        <div className="ua-stat">
          <span className="ua-stat__icon" aria-hidden="true">
            <i className="fa-solid fa-indian-rupee-sign" />
          </span>
          <div className="ua-stat__body">
            <span className="ua-stat__label">Total spent</span>
            <span className="ua-stat__value">₹{totalPayments}</span>
          </div>
        </div>
      </section>

      <section className="ua-card">
        <div className="ua-card__head">
          <div>
            <h2>Today&apos;s bookings</h2>
            <p>Sessions scheduled for today</p>
          </div>
        </div>
        {loading ? (
          <div className="ua-loading">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            Loading…
          </div>
        ) : todaysBookings.length > 0 ? (
          <div className="ua-bookings">
            {todaysBookings.map((booking) => (
              <BookingRow key={booking.bookingId} booking={booking} compact />
            ))}
          </div>
        ) : (
          <div className="ua-empty">
            <span className="ua-empty__icon" aria-hidden="true">
              <i className="fa-regular fa-calendar" />
            </span>
            <h3>No bookings today</h3>
            <p>You don&apos;t have any confirmed sessions for today.</p>
            <Link to="/all-facilities" className="ua-btn ua-btn--primary">
              Find a venue
            </Link>
          </div>
        )}
      </section>

      <section className="ua-card">
        <div className="ua-card__head">
          <div>
            <h2>Upcoming bookings</h2>
            <p>Manage your future court reservations</p>
          </div>
          <Link to="/booking" className="ua-btn ua-btn--ghost ua-btn--sm">
            View all
          </Link>
        </div>
        {loading ? (
          <div className="ua-loading">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            Loading…
          </div>
        ) : upcomingBookings.length > 0 ? (
          <div className="ua-bookings">
            {upcomingBookings.map((booking) => (
              <BookingRow
                key={booking.bookingId}
                booking={booking}
                showCancel
                onCancel={handleCancelBooking}
              />
            ))}
          </div>
        ) : (
          <div className="ua-empty">
            <span className="ua-empty__icon" aria-hidden="true">
              <i className="fa-solid fa-flag-checkered" />
            </span>
            <h3>No upcoming bookings</h3>
            <p>Book your next game and it will show up here.</p>
            <Link to="/all-facilities" className="ua-btn ua-btn--primary">
              Book a court
            </Link>
          </div>
        )}
      </section>
    </UserAccountLayout>
  );
};

export default Userdashboard;
